// Admin subscriptions API surface. M7c / P4.

import { api } from './api';
import type { Currency } from './money';
import type { InvoiceStatus } from './payments';

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';

export interface AdminSubscriptionRow {
  id: string;
  ownerType: 'family' | 'school';
  ownerId: string;
  ownerLabel: string;
  status: SubscriptionStatus;
  planId: string;
  planSlug: string;
  planTier: string;
  /** Free is a REAL plan row, so a free family holds an ordinary `active` sub. */
  isFreePlan: boolean;
  currency: Currency;
  interval: 'month' | 'year';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** An admin-granted comp. Still an ordinary subscription in every other way. */
  isComp: boolean;
  createdAt: string;
}

export interface AdminSubscriptionDetail extends AdminSubscriptionRow {
  currentPeriodStart: string | null;
  canceledAt: string | null;
  provider: string;
  providerRef: string | null;
  maxKids: number | null;
  grantedBy: string | null;
}

export interface SubscriptionInvoice {
  id: string;
  amountMinor: number;
  currency: Currency;
  status: InvoiceStatus;
  issuedAt: string;
  paidAt: string | null;
}

export interface SubscriptionCard {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
}

export async function listSubscriptions(
  status?: string,
  signal?: AbortSignal,
): Promise<AdminSubscriptionRow[]> {
  const q = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const { subscriptions } = await api<{ subscriptions: AdminSubscriptionRow[] }>(
    `/admin/subscriptions${q}`,
    { signal },
  );
  return subscriptions;
}

export async function getSubscription(
  id: string,
  signal?: AbortSignal,
): Promise<{
  subscription: AdminSubscriptionDetail;
  invoices: SubscriptionInvoice[];
  paymentMethods: SubscriptionCard[];
}> {
  return api(`/admin/subscriptions/${id}`, { signal });
}

/**
 * Comp a subscription to a family. Creates (or upgrades) an ORDINARY active
 * subscription — it expires through the same path as a paid one.
 */
export async function grantSubscription(input: {
  familyId: string;
  planId: string;
  currency: Currency;
  interval: 'month' | 'year';
  days: number;
  reason: string;
}): Promise<{ subscriptionId: string; currentPeriodEnd: string }> {
  return api('/admin/subscriptions/grant', { method: 'POST', body: input });
}

/** `immediately: false` (the default) lets paid-for access run to period end. */
export async function cancelSubscription(
  id: string,
  input: { immediately?: boolean; reason: string },
): Promise<{ ok: boolean }> {
  return api(`/admin/subscriptions/${id}/cancel`, { method: 'POST', body: input });
}
